// Point-in-time reconstruction for replays.
//
// A round stored for replay is NOT a live game state — core's
// `extractReplayGameState` strips it down to the event logs (gameEvents /
// powerUpEvents / effectEvents) plus the worm path segments, with worms and
// players reduced to bare `{id}` (no live `.alive` flag, no `gameState.effects`
// array). So the usual gsf snapshot reads (`getAlivePlayers`, `getWormEffects`)
// don't work on a replay state.
//
// These helpers rebuild the small slices the live HUD / effects panel / audio
// engine need, AS THEY WERE at a given `renderTime`, so a replay can drive the
// exact same components as a live round. Everything is derived from the logs:
//   - deaths  → gameEvents "player_died" (alive count); crashes → "worm_died" (crash SFX)
//   - effects → effectEvents "spawn" (carries the whole effect) / "despawn"
//   - worms   → path segments (each carries wormId, playerId, startTime, endTime)

// How many players are still alive at `renderTime` (one fewer per elapsed
// "player_died" event).
import * as gsf from "core/src/core/game-state-functions.js";

export function getAlivePlayerCount(gameState, renderTime) {
    var dead = {};
    gameState.gameEvents.forEach(function (e) {
        if (e.type === "player_died" && e.time <= renderTime) {
            dead[e.playerId] = true;
        }
    });
    return gameState.players.length - Object.keys(dead).length;
}

// wormId -> playerId, read off the path segments (replay worms carry no playerId).
export function getWormPlayerMap(gameState) {
    var map = {};
    var tracks = gameState.wormPathSegments;
    Object.keys(tracks).forEach(function (trackId) {
        var segments = tracks[trackId];
        if (segments.length > 0) {
            map[segments[0].wormId] = segments[0].playerId;
        }
    });
    return map;
}

// Effects active at `renderTime`, reconstructed from effectEvents. Each spawn
// carries the full effect object; the matching despawn (by effectId) ends it.
// Returns [{ effect, wormId, progress }] where progress is the remaining fraction
// of the effect's lifetime (1 at spawn → 0 at end), matching the live ring.
export function getActiveEffects(gameState, renderTime) {
    var despawnTimeById = {};
    gameState.effectEvents.forEach(function (e) {
        if (e.type === "despawn") {
            var prev = despawnTimeById[e.effectId];
            if (prev === undefined || e.time < prev) {
                despawnTimeById[e.effectId] = e.time;
            }
        }
    });

    var active = [];
    gameState.effectEvents.forEach(function (e) {
        if (e.type !== "spawn") {
            return;
        }
        var effect = e.effect;
        var spawnTime = e.time;
        if (spawnTime > renderTime) {
            return;
        }
        var endTime = despawnTimeById[effect.id];
        if (endTime !== undefined && endTime <= renderTime) {
            return; // already ended
        }

        // Prefer the real end (despawn) for the ring; fall back to the effect's
        // own duration, else treat it as full (no known end yet).
        var progress;
        if (endTime !== undefined) {
            progress = (endTime - renderTime) / (endTime - spawnTime);
        } else if (effect.duration) {
            progress = (spawnTime + effect.duration - renderTime) / effect.duration;
        } else {
            progress = 1;
        }
        progress = Math.max(0, Math.min(1, progress));

        active.push({ effect: effect, wormId: effect.wormId, progress: progress });
    });
    return active;
}

// Seconds until the round leaves its start phase, at `renderTime` (mirrors
// gsf.getStartPhaseTimeLeft, which is startPhaseTime - gameTime live).
export function getStartPhaseTimeLeft(gameState, renderTime) {
    return gameState.startPhaseTime - renderTime;
}

// Audio inputs for the music controller at `renderTime`. This mirrors
// LocalGame's computeGameAudio() but gates every cumulative count to events that
// have elapsed by `renderTime`, so the soundtrack rises and the SFX fire as the
// replay plays (rather than reading the finished round's totals all at once).
// Keep this in sync with computeGameAudio in LocalGame.js.
export function getReplayAudioState(gameState, renderTime) {
    var inStartPhase = renderTime < gameState.startPhaseTime;
    var countdownLeft = gameState.startPhaseTime - renderTime;
    var countdownLength = gsf.getCountdownLength(gameState);

    var intensity;
    if (inStartPhase) {
        intensity = 0.15;
    } else {
        var total = gameState.players.length;
        var alive = getAlivePlayerCount(gameState, renderTime);
        if (total <= 1) {
            intensity = 0.6;
        } else {
            intensity = 0.4 + 0.55 * ((total - alive) / (total - 1));
        }
    }

    // Crash SFX is per WORM wipeout ("worm_died"), not per player elimination, so a
    // twin clone crashing still thuds in a replay (mirrors computeGameAudio). Fall
    // back to "player_died" for recordings that predate the per-worm event.
    var wormDeaths = 0;
    var playerDeaths = 0;
    gameState.gameEvents.forEach(function (e) {
        if (e.time > renderTime) return;
        if (e.type === "worm_died") wormDeaths++;
        else if (e.type === "player_died") playerDeaths++;
    });
    var crashes = wormDeaths || playerDeaths;

    // Resolve power-up names from their spawn events so switcharoonies get the
    // swap SFX instead of the pickup arpeggio (a despawn only carries the id).
    var powerUpNamesById = {};
    gameState.powerUpEvents.forEach(function (e) {
        if (e.type === "spawn") powerUpNamesById[e.powerUp.id] = e.powerUp.name;
    });
    var pickups = 0;
    var switches = 0;
    gameState.powerUpEvents.forEach(function (e) {
        if (e.type === "despawn" && e.time <= renderTime) {
            if (powerUpNamesById[e.id] === "switcharoonie") switches++;
            else pickups++;
        }
    });

    return { intensity: intensity, countdownLeft: countdownLeft, countdownLength: countdownLength, crashes: crashes, pickups: pickups, switches: switches };
}
